import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { useIntl } from "react-intl-compat";
import type { AmenityOption } from "~/services/env.service";

import {
  UpdateListingPayload,
  useUpdateListingAmenitiesMutation,
  useUpdateListingMutation,
} from "../../../../components/src/services/listings.service";
import type { Listing } from "../../../../types/listings.types";

import {
  buildListingDetailsFormSchema,
  ListingDetailsFormValues,
} from "./ListingDetailsForm/ListingDetailsForm.schema";

/**
 * Builds the form for the listing-details page (title, description,
 * amenities) and wires up its save action.
 */
const useListingDetailsForm = (
  listing?: Listing,
  amenityOptions?: AmenityOption[],
) => {
  const intl = useIntl();
  const [updateListing] = useUpdateListingMutation();
  const [updateListingAmenities] = useUpdateListingAmenitiesMutation();
  const [errorMsg, setErrorMsg] = React.useState<string>();

  const form = useForm<ListingDetailsFormValues>({
    resolver: zodResolver(buildListingDetailsFormSchema({ intl })),
    values:
      listing && amenityOptions
        ? {
            title: listing.title ?? "",
            description: listing.description ?? "",
            amenities: Object.fromEntries(
              amenityOptions.map((option) => [
                option.id,
                listing.amenities.some((a) => a.name === option.name),
              ]),
            ),
            customAmenities: Object.fromEntries(
              listing.amenities
                .filter(
                  (a) =>
                    !amenityOptions.some((option) => option.name === a.name),
                )
                .map((a) => [a.name, true]),
            ),
          }
        : undefined,
  });

  const onSave = async (): Promise<boolean> => {
    setErrorMsg(undefined);
    let success = false;

    await form.handleSubmit(async (values) => {
      if (!listing) {
        return;
      }

      const { dirtyFields } = form.formState;
      const payload: UpdateListingPayload = {
        title: values.title,
        description: values.description,
      };

      try {
        const requests: Promise<unknown>[] = [
          updateListing({ listingId: listing._id, payload }).unwrap(),
        ];

        if (dirtyFields.amenities || dirtyFields.customAmenities) {
          // `values.amenities` is keyed by amenity id (see AmenitiesFields), not
          // name — map back to the real name before sending. Custom amenities
          // are already keyed by the name the host typed in.
          const amenityIdToName = new Map(
            (amenityOptions ?? []).map((option) => [option.id, option.name]),
          );

          const selectedNames = [
            ...Object.entries(values.amenities)
              .filter(([, isSelected]) => isSelected)
              .map(([id]) => amenityIdToName.get(id) ?? id),
            ...Object.entries(values.customAmenities)
              .filter(([, isSelected]) => isSelected)
              .map(([name]) => name),
          ];

          requests.push(
            updateListingAmenities({
              parentId: listing._id,
              ownerId: listing.ownerId,
              amenities: [...new Set(selectedNames)].map((name) => ({
                name,
              })),
            }).unwrap(),
          );
        }

        await Promise.all(requests);
        success = true;
      } catch {
        setErrorMsg(
          intl.formatMessage({
            id: "hosting.listing_details.submit_error",
            defaultMessage: "Something went wrong. Please try again.",
          }),
        );
      }
    })();

    return success;
  };

  return { form, onSave, errorMsg };
};

export { useListingDetailsForm };
